
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ManageAreaController } from "@/app/ui/controllers/ManageAreaController";
import {
  ResponseSummaryGeoAddressData,
  ManageAreaData,
  CourierData,
  DetailPartnerAreaDedicated
} from "@/domain/entities/ManageArea";
import { computed, defineComponent, ref, onMounted } from "vue";
// import ToggleV2 from "@/app/ui/components/toggle-v2/index.vue";
import ModalGeoAddress from "./components/modal-geo-address.vue";
import ModalConfirmDelete from "./components/modal-confirm-delete.vue";
import { ManageArea } from "@/domain/entities/ManageArea";
import EmptyState from "./components/empty-state/index.vue";
import { ListManageAreaApiRequest } from "@/data/payload/api/ManageAreaApiRequest";
import {
  parsingErrorResponse,
  whatsapp
} from "@/app/infrastructures/misc/Utils";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import ManageAreaTable from "./components/manage-area-table/index.vue";
import ModalListCourier from "./components/modal-list-courier/index.vue";
import ModalViewArea from "./components/modal-view-area.vue";
import formatDateBasedTimezoneValue from "@/app/infrastructures/misc/common-library/FormatDateBasedTimezoneValue";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import i18n from "@/i18n";
import { useRoute, useRouter } from "vue-router";
import ModalEditArea from "./components/modal-edit-area/index.vue";
import TransferAreaModal from "./components/transfer-area-modal/index.vue"

export default defineComponent({
  name: "ManageArea",
  components: {
    // ToggleV2,
    ModalGeoAddress,
    ModalViewArea,
    EmptyState,
    ModalConfirmDelete,
    ManageAreaTable,
    ModalListCourier,
    ModalEditArea,
    TransferAreaModal
  },
  setup() {
    const t = (key: string, params?: Record<string, unknown>) =>
      String(i18n.global.t(key, params));
    const router = useRouter();
    const route = useRoute();

    const openModalGeoAddress = ref(false);
    const openModalViewArea = ref(false);
    const isShowMap = ref(false);
    const onShowMap = (value: boolean) => {
      isShowMap.value = value;
    };
    const selectedData = ref(new ManageArea());
    const summaryGeoAddressData = ref(new ResponseSummaryGeoAddressData());
    const manageAreaData = ref(new ManageAreaData());
    const filter = ref(new ListManageAreaApiRequest());
    const isLoading = ref(false);
    const errorCause = ref("");
    const actionOPRefs = ref<any[]>([]);
    const isOpenSortNameType = ref(false);
    const sortByName = ref("");
    const courierData = ref<CourierData[]>([]);
    const isShowModalListCourier = ref(false);
    const isShowModalEditArea = ref(false);
    const detailData = ref<DetailPartnerAreaDedicated | null>(null);
    const isOpenModalTransferArea = ref<boolean>(false);

    const substractHeight = computed(() => {
      return summaryGeoAddressData.value.data.unassigned > 0 ? "370" : "320";
    });

    // Delete Logic
    const showDeleteModal = ref(false);
    const dataToDelete = ref(new ManageArea());
    const isDeleting = ref(false);

    const onDeleteClick = (data: ManageArea) => {
      dataToDelete.value = data;
      showDeleteModal.value = true;
    };

    const getSummaryGeoAddress = async () => {
      MainAppController.showLoading();
      try {
        const resp = await ManageAreaController.getSummaryGeoAddress();
        summaryGeoAddressData.value = resp;
      } finally {
        MainAppController.closeLoading();
      }
    };

    const getList = async (reset = false) => {
      if (reset) {
        manageAreaData.value.pagination.page = 1;
      }
      try {
        isLoading.value = true;
        filter.value.page = manageAreaData.value.pagination.page;
        const resp = await ManageAreaController.getList(filter.value);
        manageAreaData.value = resp;
      } catch (error) {
        errorCause.value = parsingErrorResponse(error).type;
      } finally {
        isLoading.value = false;
      }
    };

    const handleDelete = async () => {
      isDeleting.value = true;
      try {
        await ManageAreaController.delete(String(dataToDelete.value.uid));
        showDeleteModal.value = false;

        MainAppController.setSnackbarMessage(
          t("pickupDashboard.manageArea.deleteSuccess", {
            areaName: dataToDelete.value.areaName
          })
        );
        MainAppController.setSnackbarPositivity(true);
        MainAppController.setSnackbarTimeout(3000);
        MainAppController.setSnackbarBackgroundColor("#00875A");
        MainAppController.setSnackbarIconLeft("icon-check-white");
        MainAppController.setSnackbar(true);

        getList();
        getSummaryGeoAddress();
      } catch (error) {
        showDeleteModal.value = false;
        MainAppController.showMessageModal(
          new ModalMessageEntities({
            title: t("pickupDashboard.manageArea.deleteFailed"),
            message: t("pickupDashboard.manageArea.deleteFailedMessage"),
            image: "circle-error",
            textSuccess: t("pickupDashboard.manageArea.ok"),
            onSubmit: () => {
              MainAppController.closeMessageModal();
            }
          })
        );
      } finally {
        isDeleting.value = false;
      }
    };

    const disableShowArea = computed(() => {
      return (
        summaryGeoAddressData.value.data.assigned === 0 &&
        summaryGeoAddressData.value.data.unassigned === 0
      );
    });

    const onSort = (sort: {
      sortBy: "area_name" | "created_at" | "updated_at" | "";
      sortDirection: "ASC" | "DESC";
    }) => {
      if (sort.sortBy === "area_name") {
        sortByName.value = sort.sortDirection;
      } else {
        sortByName.value = "";
      }
      filter.value.sort = sort.sortDirection;
      filter.value.orderBy = sort.sortBy;
      getList(true);
    };

    const column = [
      {
        name: "pickupDashboard.manageArea.column.areaName",
        sortBy: "area_name",
        style: {
          width: "188.5px"
        },
        slotName: "area_name"
      },
      {
        name: "pickupDashboard.manageArea.column.dedicatedCourier",
        sortBy: "courier_id",
        style: {
          width: "188.5px"
        },
        slotName: "courier_id"
      },
      {
        name: "pickupDashboard.manageArea.column.shipmentType",
        style: {
          width: "188.5px"
        },
        slotName: "shipment_type"
      },
      {
        name: "pickupDashboard.manageArea.column.createdDate",
        sortBy: "created_at",
        style: {
          width: "188.5px"
        },
        slotName: "created_at"
      },
      {
        name: "pickupDashboard.manageArea.column.createdBy",
        style: {
          width: "188.5px"
        },
        slotName: "created_by"
      },
      {
        name: "pickupDashboard.manageArea.column.lastUpdated",
        sortBy: "updated_at",
        style: {
          width: "188.5px"
        },
        slotName: "updated_at"
      },
      {
        name: "pickupDashboard.manageArea.column.updatedBy",
        style: {
          width: "188.5px"
        },
        slotName: "updated_by"
      },
      {
        name: "pickupDashboard.manageArea.column.action",
        style: {
          width: "84px"
        },
        slotName: "action",
        isAction: true
      }
    ];

    const showAreaDistrict = (data: ManageArea) => {
      selectedData.value = data;
      openModalGeoAddress.value = true;
    };

    const formatDate = (date: string) => {
      return formatDateBasedTimezoneValue(date, "dddd, DD MMMM YYYY, HH:mm");
    };

    const toggleActionOP = (event: any, index: number) => {
      actionOPRefs.value.forEach((op, i) => {
        if (i !== index) op?.hide();
      });
      actionOPRefs.value[index]?.toggle(event);
    };

    const onSearch = (value = "") => {
      const keyword = value.trim();
      if (!!keyword && keyword.length < 3) return;
      filter.value.q = keyword;
      getList(true);
    };

    const sortNameOption = [
      {
        name: t("pickupDashboard.manageArea.sortAZ"),
        value: "ASC"
      },
      {
        name: t("pickupDashboard.manageArea.sortZA"),
        value: "DESC"
      }
    ];

    const onOpenSortNameType = () => {
      isOpenSortNameType.value = true;
    };

    const onCloseSelectSortNameType = () => {
      isOpenSortNameType.value = false;
    };

    const onSelectSortNameType = (key: string, value: string) => {
      sortByName.value = value;
      filter.value.sort = value;
      filter.value.orderBy = "area_name";
      getList(true);
    };

    const openModalListCourier = (item: ManageArea) => {
      courierData.value = item.couriers;
      isShowModalListCourier.value = true;
    };

    const closeModalListCourier = () => {
      isShowModalListCourier.value = false;
    };

    const fetchDetail = async (uid: string) => {
      try {
        MainAppController.showLoading();
        const resp = await ManageAreaController.getDetail(uid);
        detailData.value = resp;
      } catch (error) {
        detailData.value = null;
      } finally {
        MainAppController.closeLoading();
      }
    };

    const onEditArea = async (item: ManageArea, event: any, index: number) => {
      toggleActionOP(event, index);
      await fetchDetail(item.uid);
      await getSummaryGeoAddress();
      if (detailData.value) isShowModalEditArea.value = true;
    };

    const onSuccessEdit = () => {
      MainAppController.setSnackbarMessage(
        t("pickupDashboard.manageArea.editSuccess")
      );
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(3000);
      MainAppController.setSnackbarBackgroundColor("#00875A");
      MainAppController.setSnackbarIconLeft("icon-check-white");
      MainAppController.setSnackbar(true);

      getList();
      getSummaryGeoAddress();
    };

    const onErrorEdit = (error: any) => {
      const defaultMessage = t(
        "pickupDashboard.manageArea.editFailedDefaultMessage"
      );
      let message = defaultMessage;

      if (!error?.response) {
        message = t("pickupDashboard.manageArea.editFailedInternetMessage");
      }

      if (
        error?.response &&
        error?.response?.data?.message?.en.includes("uplicate")
      ) {
        message = error?.response?.data?.message?.id;
      }

      if (
        error?.response &&
        error?.response?.data?.message?.en.includes("duplicate area with")
      ) {
        message = defaultMessage;
      }

      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: t("pickupDashboard.manageArea.editFailed"),
          message,
          image: "circle-error",
          textSuccess: "Oke",
          onSubmit: () => {
            MainAppController.closeMessageModal();
          }
        })
      );
    };

    const onTransferArea = (
      data: DetailPartnerAreaDedicated,
      event: any,
      index: number
    ) => {
      toggleActionOP(event, index);
      isOpenModalTransferArea.value = true;
      detailData.value = data;
    };

    onMounted(() => {
      getSummaryGeoAddress();
      getList(true);
    });

    return {
      isSidebarAndBreadcrumbV2: computed(
        () => MainAppController.isSidebarAndBreadcrumbV2
      ),
      isShowMap,
      openModalGeoAddress,
      selectedData,
      openModalViewArea,
      onShowMap,
      summaryGeoAddressData,
      disableShowArea,
      manageAreaData,
      showDeleteModal,
      dataToDelete,
      isDeleting,
      onDeleteClick,
      handleDelete,
      getList,
      onSort,
      column,
      showAreaDistrict,
      whatsapp,
      formatDate,
      toggleActionOP,
      isLoading,
      filter,
      actionOPRefs,
      onSearch,
      sortNameOption,
      isOpenSortNameType,
      onOpenSortNameType,
      onCloseSelectSortNameType,
      onSelectSortNameType,
      sortByName,
      substractHeight,
      courierData,
      isShowModalListCourier,
      closeModalListCourier,
      openModalListCourier,
      isShowModalEditArea,
      onEditArea,
      detailData,
      onSuccessEdit,
      onErrorEdit,
      onTransferArea,
      isOpenModalTransferArea,
      formatPhoneNumber
    };
  }
});
